import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { MedicationDialog } from "@/components/MedicationDialog";
import { MedicationImport } from "@/components/MedicationImport";
import { MedSheetSync } from "@/components/MedSheetSync";
import { RefillsDue } from "@/components/RefillsDue";
import { MatchSuggestions } from "@/components/MatchSuggestions";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Plus, Trash2, Link2, AlertTriangle } from "lucide-react";

const Stat = ({ label, value, testid, warn }) => (
  <div className={`rounded-xl border bg-white px-5 py-4 ${warn ? "border-amber-300" : "border-slate-200"}`}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p data-testid={testid} className={`mt-1 text-2xl font-bold ${warn ? "text-amber-600" : "text-[#0B1F44]"}`}>{value}</p>
  </div>
);

const refillOf = (m) => {
  const d = m.date || (m.created_at || "").slice(0, 10);
  if (!d) return null;
  const t = new Date(`${d}T12:00:00`);
  t.setDate(t.getDate() + 60);
  return t.toISOString();
};

const MedRow = ({ m, sugg, onEdit, onLink, onDelete }) => (
  <tr data-testid={`med-row-${m.id}`} className="border-t border-slate-100">
    <td className="px-3 py-2.5">
      {m.patient ? <span className="font-medium text-slate-800">{m.patient.name}</span> : (
        <span className="text-slate-600">{m.source_name}
          <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
            <AlertTriangle className="h-3 w-3" />{m.match_issue || "Not matched"}</span>
          <MatchSuggestions med={m} suggestions={sugg} /></span>)}
    </td>
    <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{m.date ? fmtDate(`${m.date}T12:00:00`) : "—"}</td>
    <td data-testid={`med-refill-${m.id}`} className="whitespace-nowrap px-3 py-2.5 text-slate-500">{refillOf(m) ? fmtDate(refillOf(m)) : "—"}</td>
    <td className="px-3 py-2.5 font-medium text-slate-800">{m.medication}</td>
    <td className="px-3 py-2.5 text-slate-600">{m.dose || "—"}</td>
    <td className="px-3 py-2.5 text-slate-600">{m.frequency || "—"}</td>
    <td className="max-w-[260px] truncate px-3 py-2.5 text-slate-500" title={[m.notes, m.note].filter(Boolean).join(" — ")}>{m.notes || m.note || "—"}</td>
    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-400">{fmtDateTime(m.updated_at)}<br />{m.updated_by}</td>
    <td className="whitespace-nowrap px-3 py-2.5 text-right">
      {!m.patient && <Button size="sm" variant="outline" className="mr-1 h-7" onClick={() => onLink(m)} data-testid={`med-link-${m.id}`}><Link2 className="mr-1 h-3.5 w-3.5" />Match</Button>}
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(m)} data-testid={`med-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => onDelete(m)} data-testid={`med-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
    </td>
  </tr>
);

export default function Medications() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dlg, setDlg] = useState({ open: false, mode: "add", med: null });
  const { data, isLoading } = useQuery({ queryKey: ["medications", search.trim(), status],
    queryFn: async () => (await api.get("/medications", { params: { search: search.trim(), status } })).data });
  const del = useMutation({ mutationFn: (id) => api.delete(`/medications/${id}`),
    onSuccess: () => { toast.success("Medication removed"); qc.invalidateQueries({ queryKey: ["medications"] }); },
    onError: () => toast.error("Could not remove the medication") });
  const open = (mode, med = null) => setDlg({ open: true, mode, med });
  const rows = data?.rows || [];
  const { data: sugg = {} } = useQuery({ queryKey: ["med-suggestions"], enabled: (data?.unmatched || 0) > 0,
    queryFn: async () => (await api.get("/medications/suggestions")).data });

  return (
    <Layout title="Medications" subtitle="Each patient's medicines and doses, from your own database"
      actions={<div className="flex gap-2"><MedicationImport />
        <Button onClick={() => open("add")} className="bg-[#0B1F44] hover:bg-[#13306a]" data-testid="med-add-btn"><Plus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Add medication</span></Button></div>}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Medicines on file" value={data?.total ?? "—"} testid="med-stat-total" />
        <Stat label="Patients with medicines" value={data?.patients ?? "—"} testid="med-stat-patients" />
        <Stat label="Need matching" value={data?.unmatched ?? "—"} testid="med-stat-unmatched" warn={data?.unmatched > 0} />
      </div>
      <MedSheetSync />
      <RefillsDue />
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input data-testid="med-search" className="sm:max-w-sm" placeholder="Search patient, medicine or dose" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="flex gap-2">
          {[["all", "All"], ["unmatched", "Need matching"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={status === k ? "default" : "outline"} onClick={() => setStatus(k)} data-testid={`med-filter-${k}`}
              className={status === k ? "bg-[#0B1F44]" : ""}>{l}</Button>))}
        </div>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-3 py-2">Patient</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Refill</th><th className="px-3 py-2">Medicine</th><th className="px-3 py-2">Dose</th>
              <th className="px-3 py-2">How often</th><th className="px-3 py-2">Notes</th><th className="px-3 py-2">Last updated</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((m) => <MedRow key={m.id} m={m} sugg={m.patient ? null : sugg[m.source_name]} onEdit={(x) => open("edit", x)} onLink={(x) => open("link", x)}
              onDelete={(x) => window.confirm(`Remove ${x.medication} for ${x.patient?.name || x.source_name}?`) && del.mutate(x.id)} />)}
          </tbody>
        </table>
        {!isLoading && rows.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-slate-500" data-testid="med-empty">
            {data?.total ? "No medicines match your search." : "No medicines yet. Import a CSV or Excel export from your database, or add one by hand."}</p>)}
        {data && data.shown_of > rows.length && <p className="px-4 py-2 text-xs text-slate-400">Showing first {rows.length} of {data.shown_of} — search to narrow down.</p>}
      </div>
      <p className="mt-3 text-xs text-slate-500">File columns needed: patient name (or First name + Last name) and Medication. Dose, Frequency and Notes are picked up when present.</p>
      <MedicationDialog open={dlg.open} mode={dlg.mode} med={dlg.med} onOpenChange={(o) => setDlg({ ...dlg, open: o })} />
    </Layout>
  );
}
